import {
  sendCustomLog,
  sendLog,
} from '@profile-ui/shared-components';

/**
 * Fetches fallback content from local static file when Contentful API fails.
 * @param {string} isoLocale - The locale code for logging purposes
 * @returns {Object} - Fallback content data
 */
const processContentfulFallback = async (fallbackUrl) => {
  let fallbackData = { fields: { dictionaryJson: {} }};
  try {
    const response = await fetch(fallbackUrl);
    fallbackData = await response.json();
    sendCustomLog(
      'ContentfulFallback',
      'Successfully fetched fallback content for locale: en-US'
    );
  } catch (error) {
    sendLog(
      'Error while fetching fallback content for locale: en-US',
      '',
      'ContentfulFallback'
    );
  }

  return fallbackData;
};

const fetchContentfulContent = async (apiUrl, isoLocale, fallbackUrl) => {
  try {
    sendCustomLog(
      'fetchContentfulContent',
      `Fetching content for locale: ${isoLocale}`
    );
    const response = await fetch(apiUrl);

    if (!response.ok) {
      sendLog(
        `Contentful API returned ${response.status} for locale: ${isoLocale}`,
        '',
        'fetchContentfulContent'
      );
      return processContentfulFallback(fallbackUrl);
    }

    return await response.json();
  } catch (error) {
    sendLog(
      `Error while fetching content for locale: ${isoLocale}`,
      '',
      'fetchContentfulContent'
    );
    return processContentfulFallback(fallbackUrl);
  }
};

/**
 * Parses flat Contentful data into a nested structure for i18n resource bundles.
 *
 * Input format: { "namespace.component.KEY": "value", ... }
 * Output format: { "namespace.component": { "KEY": "value", ... }, ... }
 *
 * @param {Object} rawData - Flat key-value object with dot-separated keys
 * @returns {Object} - Nested object grouped by namespace.component
 */
const parseContentfulData = (rawData) => {
  const dict = rawData.fields.dictionaryJson;
  const result = {};

  for (const fullKey in dict) {
    // Split the key into parts: namespace.component.KEY
    const parts = fullKey.split('.');

    // If key doesn't have at least 3 parts, skip or handle as-is
    if (parts.length < 3) continue;

    // First two parts form the namespace (e.g., "personalInfo.serverErrorMessage")
    const namespace = `${parts[0]}.${parts[1]}`;
    // Remaining parts form the actual key (e.g., "UP_IMG_001")
    const key = parts.slice(2).join('.');

    if (!result[namespace]) {
      result[namespace] = {};
    }

    result[namespace][key] = dict[fullKey];
  }

  return result;
};

export { fetchContentfulContent, parseContentfulData, processContentfulFallback };
